import type { AudioService } from "../audio/ports/AudioService";
import type { AutoSpinStore } from "../autospin/AutoSpinStore";
import type { FeatureStateStore } from "../feature-state/FeatureStateStore";
import type { BetLinesPort, BetLinesSnapshot } from "./BetLinesPort";
import type { BetSystem } from "./BetSystemTypes";

/** The coordinator's one fact this service reads. */
interface BetChangeSource {
  getSnapshot(): { canChangeBet: boolean };
  subscribe(listener: () => void): () => void;
}

/**
 * The shell's side of `BetLinesPort` (decision 56). Built with the services, before the presentation that takes it;
 * the coordinator arrives with the control bar (`bind`), and until then no choice is taken. A choice follows the bet
 * controls' rule — idle with the bet unlocked, and hidden there in a feature or while autoplay runs, never in a replay
 * — and plays their cue, up or down.
 */
export class BetLinesService implements BetLinesPort {
  private coordinator: BetChangeSource | null = null;
  private readonly listeners = new Set<() => void>();

  public constructor(
    private readonly deps: {
      betSystem: BetSystem;
      autoSpinStore: AutoSpinStore;
      featureStateStore: FeatureStateStore;
      audioService: AudioService;
      replayMode: boolean;
    },
  ) {
    deps.betSystem.subscribe(() => this.emit());
    deps.autoSpinStore.subscribe(() => this.emit());
    deps.featureStateStore.subscribe(() => this.emit());
  }

  /** The control bar's coordinator: from here on a choice can be taken. */
  public bind(coordinator: BetChangeSource): void {
    this.coordinator = coordinator;
    coordinator.subscribe(() => this.emit());
    this.emit();
  }

  public getSnapshot(): BetLinesSnapshot {
    const bet = this.deps.betSystem;
    return { lines: bet.getLines(), options: bet.getLineOptions(), lineBetMinor: bet.getLineBetMinor(), canChange: this.canChange() };
  }

  public select(lines: number): boolean {
    const bet = this.deps.betSystem;
    if (!this.canChange() || !bet.getLineOptions().includes(lines) || lines === bet.getLines()) return false;
    const up = lines > bet.getLines();
    bet.setLines(lines);
    this.deps.audioService.presentation.emit({ id: up ? "bet.increase" : "bet.decrease" });
    return true;
  }

  public subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private canChange(): boolean {
    if (this.deps.replayMode || !this.coordinator?.getSnapshot().canChangeBet) return false;
    return !this.deps.autoSpinStore.getSnapshot().running && !this.deps.featureStateStore.isFeatureActive();
  }

  private emit(): void {
    for (const listener of [...this.listeners]) listener();
  }
}
